import { defineStore } from 'pinia';
import { defaultStag, stagAfter14days } from '~/config/constants.config';
import { getCookieValue, rootDomain } from '~/utils/cookie';
import { useClickTracker } from '../../composables/useClickTracker';
import { useStoreSsLocales } from '../softswiss/locales';
import { useStoreSsPlayer } from '../softswiss/player';

const encodeBase64 = (str: string) => btoa(str);
const decodeBase64 = (str: string) => atob(str);

const STORAGE_KEY = 'appservicepartner';
const LIFETIME_KEY = 'appservicepartner-lifetime';

const stagToPureStag = (stag: string) => {
  const splittedStag = String(stag).split('_');
  if (splittedStag && splittedStag.length) {
    return splittedStag[0];
  }
  return '';
};

export const useStoreStag = defineStore('clientStag', {
  state: () => ({
    stag: '',
    lastSavedStag: '',
    wasRegisteredByPureStag: '',
    uniq_id: '',
  }),

  getters: {
    currentStag(state): string {
      if (state.stag && !state.stag.includes('_')) {
        const result = state.stag + '_noclickid';
        return result;
      }
      if (state.stag) {
        return state.stag;
      }
      return defaultStag;
    },

    currentPureStag(): string {
      let raw = '';
      if (this.currentStag) {
        raw = this.currentStag;
      } else if (localStorage[STORAGE_KEY]) {
        raw = decodeBase64(localStorage[STORAGE_KEY]);
      }
      const pure = stagToPureStag(raw);
      return pure;
    },
  },

  actions: {
    checkStag(stag?: string) {
      const player = useStoreSsPlayer();

      if (!stag) {
        stag = localStorage.getItem(STORAGE_KEY)
          ? decodeBase64(localStorage.getItem(STORAGE_KEY) || '')
          : '';
      }

      if (!import.meta.browser || player.isPlayerAuthorised) {
        return;
      }

      if (!window || !window.location) {
        return;
      }

      useClickTracker().generateAndStoreUniqId();

      const urlQueryParams = new URLSearchParams(window.location.search);

      const refCodeValue = urlQueryParams.get('ref_code');
      if (refCodeValue) {
        useStoreSsLocales().saveRefCode(refCodeValue);
      }

      if (refCodeValue || getCookieValue('ref_code')) {
        this.stag = '';
        this.wasRegisteredByPureStag = '';
        localStorage.removeItem(STORAGE_KEY);

        if (localStorage.getItem(STORAGE_KEY)) {
          const domain = rootDomain(document.location.host);
          document.cookie = `${STORAGE_KEY}=; max-age=-1000; domain=${domain}; path=/;`;
        }

        return;
      }

      const stagParams = window.location.href.match(/stag=(\d+(_[\d\w]+)?)/);
      if (stagParams && stagParams[1]) {
        this.stag = stagParams[1];
      } else if (stag) {
        this.stag = stag;
      }

      console.log(this.currentStag);

      if (this.currentStag) {
        if (!localStorage.getItem(STORAGE_KEY)) {
          useStoreSsLocales().saveStag(this.currentStag, useStoreStag().uniq_id);
          localStorage[STORAGE_KEY] = encodeBase64(this.currentStag);
        } else if (decodeBase64(localStorage.getItem(STORAGE_KEY) || '') !== this.currentStag) {
          useStoreSsLocales().saveStag(this.currentStag, useStoreStag().uniq_id);
          localStorage[STORAGE_KEY] = encodeBase64(this.currentStag);
        }
      }

      const stagLifetime = getCookieValue(LIFETIME_KEY);

      const isExpiredStag =
        stagLifetime && Number(stagLifetime) < Date.now() && !stag.includes(defaultStag);
      if (
        isExpiredStag ||
        (!urlQueryParams.has('stag') && !localStorage.getItem(STORAGE_KEY)) ||
        (!getCookieValue('stag') && stagLifetime)
      ) {
        const stag = isExpiredStag ? stagAfter14days : defaultStag;
        useStoreSsLocales().saveStag(stag, useStoreStag().uniq_id);
        localStorage.setItem(STORAGE_KEY, encodeBase64(stag));
        this.stag = stag;
        window.document.cookie = `${LIFETIME_KEY}=;Max-Age=-1000;Path=/`;
      }

      if (!this.currentPureStag) {
        console.log('no stag');
        const defaultStagRaw = '157007';
        this.stag = defaultStagRaw;
        useStoreSsLocales().saveStag(defaultStagRaw, useStoreStag().uniq_id);
        localStorage.setItem(STORAGE_KEY, encodeBase64(defaultStagRaw));
      }

      if (!getCookieValue(LIFETIME_KEY)) {
        const expire = new Date();
        expire.setMonth(expire.getMonth() + 1);
        window.document.cookie = `${LIFETIME_KEY}=${
          Date.now() + 3600000
        };expires=${expire.toUTCString()};Path=/`;
      }
    },

    setStag(stag: string) {
      useStoreSsLocales().saveStag(stag, useStoreStag().uniq_id);
      localStorage.setItem(STORAGE_KEY, encodeBase64(stag));
      this.stag = stag;
      window.document.cookie = `${LIFETIME_KEY}=;Max-Age=-1000;Path=/`;
      this.lastSavedStag = stag;
    },

    /*
    Sometimes we need to know if user had stag in past
    for example to show sign_up modal if user reloads page
    */
    checkPureStagByWhichWasRegistered() {
      if (localStorage && localStorage?.[STORAGE_KEY]) {
        this.wasRegisteredByPureStag = stagToPureStag(decodeBase64(localStorage[STORAGE_KEY]));
      }
    },

    setUniqId(payload: string) {
      this.uniq_id = payload;
    },
  },
});
